import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import MaterialPlanTab from './MaterialPlanTab';
import LaborPlanTab from './LaborPlanTab';
import TimePlanTab from './TimePlanTab';
import {
  Clock, Brain, ShoppingCart, Share2, Pen, Check, Loader2, Lock,
  TrendingUp, AlertTriangle, Copy, Printer, Download, Target
} from 'lucide-react';
import GeofenceTab from './GeofenceTab';
import ProjectStatsGrid from './components/ProjectStatsGrid';
import CostReportCard from './components/CostReportCard';
import ProjectMachinesCard from './components/ProjectMachinesCard';
import ProjectWizard from './ProjectWizard';
import { ProjectHeader } from './project/ProjectHeader';

const ProjectDetail = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'material-plan';
  const autorun = searchParams.get('autorun') === '1';
  const autorunHandledRef = useRef(false);
  const [tab, setTab] = useState(initialTab);
  const goTab = (v) => {
    setTab(v);
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('tab', v); return n; }, { replace: true });
  };
  // viewMode borttagen — alltid guidad wizard-läge
  const [project, setProject] = useState(null);
  const [timeReports, setTimeReports] = useState([]);
  const [machineHours, setMachineHours] = useState([]);
  const [etaReports, setETAReports] = useState([]);
  const [shoppingLists, setShoppingLists] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [customerAccess, setCustomerAccess] = useState([]);
  const [materialPlansCount, setMaterialPlansCount] = useState(0);
  const [laborPlansCount, setLaborPlansCount] = useState(0);
  const [timePlansCount, setTimePlansCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [estimating, setEstimating] = useState(false);
  const [progressDialog, setProgressDialog] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareForm, setShareForm] = useState({ customer_email: '', customer_name: '' });
  const [signDialog, setSignDialog] = useState(false);
  const [signAction, setSignAction] = useState('');
  const [budgetForm, setBudgetForm] = useState({ budget_material: '', budget_labor: '', budget_machines: '' });
  const [savingBudget, setSavingBudget] = useState(false);
  const [costSummary, setCostSummary] = useState(null);
  const [rotEnabled, setRotEnabled] = useState(false);
  const [showCostEstimate, setShowCostEstimate] = useState(false);
  const [estimateDesc, setEstimateDesc] = useState('');
  const [costEstimating, setCostEstimating] = useState(false);
  const [aiEstimate, setAiEstimate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectRes, timeRes, machineRes, etaRes, sigRes, accessRes, shopRes] = await Promise.all([
        api.getProject(id),
        api.getTimeReports({ project_id: id }),
        api.getMachineHours({ project_id: id }),
        api.getETAReports({ project_id: id }),
        api.getSignatures({ project_id: id }),
        api.getCustomerAccessList(id).catch(() => ({ data: [] })),
        api.getShoppingLists(id).catch(() => ({ data: [] })),
      ]);
      setProject(projectRes.data);
      setTimeReports(timeRes.data);
      setMachineHours(machineRes.data);
      setETAReports(etaRes.data);
      setSignatures(sigRes.data || []);
      setCustomerAccess(accessRes.data || []);
      setShoppingLists(shopRes.data || []);
      setProgressValue(projectRes.data.progress_percent || 0);
      setBudgetForm({
        budget_material: projectRes.data.budget_material || '',
        budget_labor: projectRes.data.budget_labor || '',
        budget_machines: projectRes.data.budget_machines || '',
      });
      setRotEnabled(projectRes.data.rot_eligible || false);
      // Fetch cost summary + material-plan count (async, non-blocking)
      api.getProjectCostSummary(id).then(r => setCostSummary(r.data)).catch(() => {});
      api.getMaterialPlans(id).then(r => {
        const plans = r.data?.material_plans || r.data || [];
        setMaterialPlansCount(Array.isArray(plans) ? plans.filter(p => p.status === 'completed').length : 0);
      }).catch(() => setMaterialPlansCount(0));
      api.listLaborPlans(id).then(r => {
        const plans = r.data?.plans || [];
        setLaborPlansCount(plans.filter(p => p.status === 'completed').length);
      }).catch(() => setLaborPlansCount(0));
      api.listTimePlans(id).then(r => {
        const plans = r.data?.plans || [];
        setTimePlansCount(plans.filter(p => p.status === 'completed').length);
      }).catch(() => setTimePlansCount(0));
    } catch (error) {
      toast.error('Kunde inte hämta projektdata');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTimeReport = async (reportId) => {
    try {
      await api.updateTimeReportStatus(reportId, 'approved');
      toast.success('Tidrapport godkänd');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte godkänna');
    }
  };

  const handleRejectTimeReport = async (reportId) => {
    try {
      await api.updateTimeReportStatus(reportId, 'rejected');
      toast.success('Tidrapport nekad');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte neka');
    }
  };

  const handleAITimeEstimate = async () => {
    setEstimating(true);
    try {
      const res = await api.getAITimeEstimate(id);
      toast.success('AI-tidsuppskattning klar!');
      fetchData();
    } catch (error) {
      toast.error('AI-tidsuppskattning misslyckades');
    } finally {
      setEstimating(false);
    }
  };

  // Autorun AI when navigated here from dashboard quick-calc buttons
  useEffect(() => {
    if (!autorun || autorunHandledRef.current || !project) return;
    const tab = searchParams.get('tab');
    autorunHandledRef.current = true;
    // Clear the autorun param so reload doesn't re-trigger
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('autorun');
      return next;
    }, { replace: true });

    if (tab === 'ai-estimate') {
      handleAITimeEstimate();
    } else if (tab === 'budget') {
      setShowCostEstimate(true);
      if (!estimateDesc && project?.description) setEstimateDesc(project.description);
    } else if (tab === 'material-plan') {
      // MaterialPlanTab listens to autorun via window event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('operio-autorun-material-plan'));
      }, 300);
    }
  }, [autorun, project, searchParams, setSearchParams, estimateDesc]);

  const handleUpdateProgress = async () => {
    try {
      await api.updateProjectProgress(id, { progress_percent: progressValue });
      toast.success('Progress uppdaterad');
      setProgressDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Kunde inte uppdatera progress');
    }
  };

  const handleShareProject = async () => {
    if (!shareForm.customer_email) { toast.error('Ange e-post'); return; }
    try {
      const res = await api.createCustomerAccess({
        project_id: id,
        customer_email: shareForm.customer_email,
        customer_name: shareForm.customer_name || null,
      });
      const link = `${window.location.origin}/customer/project/${res.data.access_token}`;
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Kundlänk skapad och kopierad!');
      setShareDialog(false);
      setShareForm({ customer_email: '', customer_name: '' });
      fetchData();
    } catch (error) {
      toast.error('Kunde inte skapa kundåtkomst');
    }
  };

  const handleSign = async () => {
    if (!signAction) return;
    try {
      await api.createSignature({
        project_id: id,
        action: signAction,
        reference_id: id,
      });
      toast.success('Signerat med BankID (mockad)');
      setSignDialog(false);
      setSignAction('');
      fetchData();
    } catch (error) {
      toast.error('Signering misslyckades');
    }
  };

  const handleSaveBudget = async () => {
    setSavingBudget(true);
    try {
      const bm = parseFloat(budgetForm.budget_material) || 0;
      const bl = parseFloat(budgetForm.budget_labor) || 0;
      const bmach = parseFloat(budgetForm.budget_machines) || 0;
      await api.updateProject(id, {
        ...project,
        start_date: project.start_date || null,
        end_date: project.end_date || null,
        budget_material: bm || null,
        budget_labor: bl || null,
        budget_machines: bmach || null,
        budget: (bm + bl + bmach) || project.budget || null,
        rot_eligible: rotEnabled,
      });
      toast.success('Budget sparad!');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte spara budget');
    } finally {
      setSavingBudget(false);
    }
  };

  const handleAiEstimate = async () => {
    if (!estimateDesc.trim()) { toast.error('Ange en jobbeskrivning'); return; }
    setCostEstimating(true);
    try {
      const res = await api.aiCostEstimate({ description: estimateDesc, location: project?.location, work_type: 'Bygg' });
      setAiEstimate(res.data);
    } catch (err) {
      toast.error('AI-kalkyl misslyckades');
    } finally {
      setCostEstimating(false);
    }
  };

  const applyEstimateToBudget = () => {
    if (!aiEstimate) return;
    setBudgetForm({
      budget_material: aiEstimate.material?.total || '',
      budget_labor: aiEstimate.labor?.total || '',
      budget_machines: aiEstimate.machines?.total || '',
    });
    toast.success('AI-kalkyl applicerad på budget');
    setShowCostEstimate(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="project-detail-loading">
      <div className="w-6 h-6 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  if (!project) return <div className="text-muted-foreground">Projekt hittades inte</div>;

  const totalHours = timeReports.reduce((sum, tr) => sum + tr.hours, 0);
  const totalMachineHours = machineHours.reduce((sum, mh) => sum + mh.hours, 0);
  const progress = project.progress_percent || 0;
  const progressColor = progress >= 80 ? 'bg-green-500' : progress >= 40 ? 'bg-yellow-500' : progress > 0 ? 'bg-[hsl(16,100%,55%)]' : 'bg-muted';
  const statusBadge = project.status === 'completed' ? 'bg-green-50 text-green-700'
    : project.status === 'paused' ? 'bg-yellow-50 text-yellow-700'
    : 'bg-blue-50 text-blue-700';
  const estimate = project.ai_time_estimate;
  const steps = [
    { tab: 'material-plan', label: 'Material', done: materialPlansCount > 0, locked: false },
    { tab: 'labor-plan', label: 'Arbete', done: laborPlansCount > 0, locked: materialPlansCount === 0 },
    { tab: 'time-plan', label: 'Tid', done: timePlansCount > 0, locked: laborPlansCount === 0 },
    { tab: 'overview', label: 'Översikt & offert', done: !!project.quote_generated_at, locked: !(project.setup_status === 'started' || (materialPlansCount > 0 && laborPlansCount > 0 && timePlansCount > 0)) },
  ];

  return (
    <div className="space-y-6 max-w-[1680px] mx-auto" data-testid="project-detail-page">
      <ProjectHeader project={project} steps={steps} onGoTab={goTab} onShare={() => setShareDialog(true)}
        onQuoteDone={() => { api.getProject(id).then((res) => setProject(res.data)); goTab('overview'); }} />

      {project.setup_status === 'started' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 flex items-center gap-3" data-testid="project-locked-banner">
          <Check size={18} className="text-emerald-600 flex-shrink-0" />
          <p className="text-xs"><span className="font-bold text-emerald-800">Projektet är startat.</span><span className="text-emerald-700 ml-1">Låst för redigering. Skapa ÄTA för ändringar.</span></p>
        </div>
      )}

      <Tabs value={tab} onValueChange={goTab} className="w-full">
        <TabsList className="bg-transparent p-0 h-auto w-full justify-start rounded-none border-b border-stone-200 overflow-x-auto op-noscroll flex-nowrap gap-6" data-testid="project-tabs">
          {steps.map((s, i) => (
            <TabsTrigger key={s.tab} value={s.tab} data-testid={`tab-${s.tab}`} disabled={s.locked}
              className="flex-shrink-0 rounded-none bg-transparent px-0 pb-3 pt-1 text-[13.5px] font-semibold text-stone-500 border-b-2 border-transparent data-[state=active]:border-[#F26B1F] data-[state=active]:text-stone-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none gap-2">
              <span className={`w-5 h-5 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${s.done ? 'bg-emerald-500 text-white' : 'bg-stone-200 text-stone-600'}`}>{s.done ? <Check size={11} /> : i + 1}</span>
              {s.label}{s.locked && <Lock size={11} className="opacity-50" />}
            </TabsTrigger>
          ))}
          {project.setup_status === 'started' && [['time-reports', 'Tidrapporter'], ['eta', 'ÄTA'], ['machine-hours', 'Maskintimmar'], ['geofence', 'Geofence'], ['sharing', 'Kundåtkomst']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="flex-shrink-0 rounded-none bg-transparent px-0 pb-3 pt-1 text-[13.5px] font-semibold text-stone-500 border-b-2 border-transparent data-[state=active]:border-[#F26B1F] data-[state=active]:text-stone-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none">{l}</TabsTrigger>
          ))}
          <TabsTrigger value="budget" data-testid="tab-budget" className="hidden">Budget</TabsTrigger>
          <TabsTrigger value="ai-estimate" data-testid="tab-ai-estimate" className="hidden">AI</TabsTrigger>
        </TabsList>

        {/* Overview — Combined Rapport (#5) */}
        <TabsContent value="overview" className="mt-6 space-y-4">
          {project.setup_status !== 'started' && (
            <ProjectWizard
              project={project}
              projectStatus={{
                materialPlanCount: materialPlansCount,
                laborPlanCount: laborPlansCount,
                timePlanCount: timePlansCount,
                timeReportCount: timeReports.length,
                hasTimeEstimate: !!project.ai_time_estimate,
                hasInvoice: !!project.auto_invoice_id,
              }}
              onSwitchTab={goTab}
              onProjectStarted={() => { api.getProject(id).then((res) => setProject(res.data)); }}
            />
          )}
          <ProjectStatsGrid
            totalHours={totalHours}
            totalMachineHours={totalMachineHours}
            etaCount={etaReports.length}
            signatureCount={signatures.length}
            costSummary={costSummary}
          />
          <CostReportCard costSummary={costSummary} />
        </TabsContent>

        {/* Budget & Kalkyl */}
        <TabsContent value="budget" className="mt-6 space-y-6">
          <div className="bg-card border border-border rounded p-6">
            <h3 className="text-base font-bold mb-4">Budgetstruktur</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-blue-500" /> Material
                </Label>
                <Input
                  type="number" placeholder="0"
                  value={budgetForm.budget_material}
                  onChange={e => setBudgetForm(f => ({ ...f, budget_material: e.target.value }))}
                  data-testid="budget-material-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-green-500" /> Arbete
                </Label>
                <Input
                  type="number" placeholder="0"
                  value={budgetForm.budget_labor}
                  onChange={e => setBudgetForm(f => ({ ...f, budget_labor: e.target.value }))}
                  data-testid="budget-labor-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-amber-500" /> Maskiner
                </Label>
                <Input
                  type="number" placeholder="0"
                  value={budgetForm.budget_machines}
                  onChange={e => setBudgetForm(f => ({ ...f, budget_machines: e.target.value }))}
                  data-testid="budget-machines-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
              <div>
                <span className="text-sm text-muted-foreground">Totalbudget: </span>
                <span className="text-lg font-bold text-foreground">
                  {((parseFloat(budgetForm.budget_material) || 0) + (parseFloat(budgetForm.budget_labor) || 0) + (parseFloat(budgetForm.budget_machines) || 0)).toLocaleString('sv-SE')} kr
                </span>
              </div>
              <Button onClick={handleSaveBudget} disabled={savingBudget} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="save-budget-btn">
                {savingBudget ? 'Sparar...' : 'Spara budget'}
              </Button>
              <Button variant="outline" onClick={() => setShowCostEstimate(true)} data-testid="ai-estimate-btn">
                <Brain size={14} className="mr-1.5" /> AI-kalkyl
              </Button>
            </div>
          </div>

          {/* Cost Breakdown with actual data */}
          <div className="bg-card border border-border rounded p-6">
            <h3 className="text-base font-bold mb-4">Kostnadsuppdelning (faktisk)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-4">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">Material</p>
                <p className="text-2xl font-bold text-foreground mt-1" data-testid="cost-material">
                  {costSummary ? `${costSummary.material_cost.toLocaleString('sv-SE')} kr` : '—'}
                </p>
                {project.budget_material > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Budget: {project.budget_material?.toLocaleString('sv-SE')} kr
                  </div>
                )}
              </div>
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded p-4">
                <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">Arbete</p>
                <p className="text-2xl font-bold text-foreground mt-1" data-testid="cost-labor">
                  {costSummary ? `${costSummary.labor_cost.toLocaleString('sv-SE')} kr` : '—'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {costSummary ? `${costSummary.labor_hours} tim × ${costSummary.labor_rate} kr/tim` : '—'}
                </p>
                {project.budget_labor > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Budget: {project.budget_labor?.toLocaleString('sv-SE')} kr
                  </div>
                )}
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-4">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider">Maskiner</p>
                <p className="text-2xl font-bold text-foreground mt-1" data-testid="cost-machines">
                  {costSummary ? `${costSummary.machine_cost.toLocaleString('sv-SE')} kr` : '—'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {costSummary ? `${costSummary.machine_hours} tim × ${costSummary.machine_rate} kr/tim` : '—'}
                </p>
                {project.budget_machines > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Budget: {project.budget_machines?.toLocaleString('sv-SE')} kr
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ROT-avdrag (#8) */}
          <div className="bg-card border border-border rounded p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold">ROT-avdrag</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  ROT-avdrag ger kunden 30% rabatt på arbetskostnaden (max 50 000 kr/person/år)
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="rot-toggle">
                <span className="text-sm font-medium">{rotEnabled ? 'Aktivt' : 'Inaktivt'}</span>
                <div
                  onClick={() => setRotEnabled(!rotEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${rotEnabled ? 'bg-emerald-500' : 'bg-muted'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${rotEnabled ? 'translate-x-5' : ''}`} />
                </div>
              </label>
            </div>
            {rotEnabled && costSummary && (
              <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground">Pris före ROT</p>
                  <p className="text-lg font-bold text-foreground">{costSummary.total_cost.toLocaleString('sv-SE')} kr</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">ROT-avdrag (30% av arbete)</p>
                  <p className="text-lg font-bold text-emerald-600">-{(costSummary.labor_cost * 0.3).toLocaleString('sv-SE')} kr</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pris efter ROT</p>
                  <p className="text-lg font-bold text-foreground">{(costSummary.total_cost - costSummary.labor_cost * 0.3).toLocaleString('sv-SE')} kr</p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* AI Time Estimate */}
        <TabsContent value="ai-estimate" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold tracking-tight">AI Tidsuppskattning</h3>
            <Button onClick={handleAITimeEstimate} disabled={estimating} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="ai-estimate-btn">
              {estimating ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyserar...</> : <><Brain size={14} className="mr-1.5" /> Beräkna med AI</>}
            </Button>
          </div>
          {estimate ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded p-5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Totalt dagar</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{estimate.estimated_total_days}</p>
                </div>
                <div className="bg-card border border-border rounded p-5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Kvarvarande dagar</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{estimate.estimated_remaining_days}</p>
                </div>
                <div className="bg-card border border-border rounded p-5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Rec. arbetare</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{estimate.estimated_workers_needed}</p>
                </div>
                <div className="bg-card border border-border rounded p-5">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Konfidens</p>
                  <p className={`text-2xl font-bold mt-1 ${estimate.confidence === 'high' ? 'text-green-600' : estimate.confidence === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                    {estimate.confidence === 'high' ? 'Hög' : estimate.confidence === 'medium' ? 'Medel' : 'Låg'}
                  </p>
                </div>
              </div>
              <div className="bg-card border border-border rounded p-5">
                <h4 className="text-sm font-bold mb-2">Motivering</h4>
                <p className="text-sm text-muted-foreground">{estimate.reasoning}</p>
              </div>
              {estimate.milestones?.length > 0 && (
                <div className="bg-card border border-border rounded p-5">
                  <h4 className="text-sm font-bold mb-3">Milstolpar</h4>
                  <div className="space-y-3">
                    {estimate.milestones.map((m, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-[hsl(16,100%,55%)]/10 text-[hsl(16,100%,55%)] rounded flex items-center justify-center text-xs font-bold flex-shrink-0">{m.estimated_day || i + 1}</div>
                        <div>
                          <p className="text-sm font-medium">{m.name}</p>
                          <p className="text-xs text-muted-foreground">{m.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {estimate.risks?.length > 0 && (
                <div className="bg-card border border-destructive/30 rounded p-5">
                  <h4 className="text-sm font-bold text-destructive mb-2 flex items-center gap-1.5"><AlertTriangle size={14} /> Risker</h4>
                  <ul className="space-y-1">
                    {estimate.risks.map((r, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-destructive mt-0.5">•</span> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border rounded p-8 text-center">
              <Brain size={32} className="text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Klicka "Beräkna med AI" för att få en tidsuppskattning baserad på projektdata.</p>
            </div>
          )}
        </TabsContent>

        {/* Time Reports */}
        <TabsContent value="time-reports" className="mt-6">
          <div className="bg-card border border-border rounded">
            {timeReports.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Inga tidrapporter</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Datum</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Anställd</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timmar</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Åtgärd</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {timeReports.map((tr) => (
                      <tr key={tr.id} data-testid={`time-report-${tr.id}`}>
                        <td className="px-5 py-3 text-sm">{new Date(tr.date).toLocaleDateString('sv-SE')}</td>
                        <td className="px-5 py-3 text-sm">{tr.employee_id?.substring(0, 8)}</td>
                        <td className="px-5 py-3 text-sm font-medium">{tr.hours}h</td>
                        <td className="px-5 py-3 text-sm">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            tr.status === 'approved' ? 'bg-green-50 text-green-700' :
                            tr.status === 'rejected' ? 'bg-red-50 text-red-700' :
                            'bg-yellow-50 text-yellow-700'
                          }`}>{tr.status}</span>
                        </td>
                        <td className="px-5 py-3 text-sm">
                          {tr.status === 'pending' && (
                            <div className="flex gap-2">
                              <button onClick={() => handleApproveTimeReport(tr.id)} data-testid={`approve-${tr.id}`} className="text-xs text-green-700 hover:underline font-medium">Godkänn</button>
                              <button onClick={() => handleRejectTimeReport(tr.id)} data-testid={`reject-${tr.id}`} className="text-xs text-red-700 hover:underline font-medium">Neka</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Machine Hours */}
        <TabsContent value="machine-hours" className="mt-6">
          <ProjectMachinesCard project={project} onUpdated={fetchData} />
          <div className="bg-card border border-border rounded">
            {machineHours.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Inga maskintimmar</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Datum</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Maskin</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timmar</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Kommentar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {machineHours.map((mh) => (
                      <tr key={mh.id}>
                        <td className="px-5 py-3 text-sm">{new Date(mh.date).toLocaleDateString('sv-SE')}</td>
                        <td className="px-5 py-3 text-sm">{mh.machine_id?.substring(0, 8)}</td>
                        <td className="px-5 py-3 text-sm font-medium">{mh.hours}h</td>
                        <td className="px-5 py-3 text-sm text-muted-foreground">{mh.comment || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ETA */}
        <TabsContent value="eta" className="mt-6">
          <div className="bg-card border border-border rounded">
            {etaReports.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Inga ÄTA-rapporter</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Datum</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Titel</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">PDF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {etaReports.map((eta) => (
                      <tr key={eta.id}>
                        <td className="px-5 py-3 text-sm">{new Date(eta.date).toLocaleDateString('sv-SE')}</td>
                        <td className="px-5 py-3 text-sm font-medium">{eta.title}</td>
                        <td className="px-5 py-3 text-sm"><span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">{eta.status}</span></td>
                        <td className="px-5 py-3 text-sm">
                          <button
                            onClick={async () => {
                              try {
                                const res = await api.downloadEtaPdf(eta.id);
                                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                const a = document.createElement('a'); a.href = url; a.download = `ata-${eta.title}.pdf`;
                                document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                              } catch (err) { toast.error('Kunde inte generera PDF'); }
                            }}
                            className="p-1.5 text-muted-foreground hover:text-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,55%)]/10 rounded transition-colors"
                            title="Ladda ner PDF"
                            data-testid={`download-eta-${eta.id}`}
                          >
                            <Download size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Material Plan */}
        <TabsContent value="material-plan" className="mt-6">
          <MaterialPlanTab projectId={id} project={project} />
        </TabsContent>

        <TabsContent value="labor-plan" className="mt-6">
          <LaborPlanTab projectId={id} />
        </TabsContent>

        <TabsContent value="time-plan" className="mt-6">
          <TimePlanTab projectId={id} />
        </TabsContent>

        {/* Geofence */}
        <TabsContent value="geofence" className="mt-6">
          <GeofenceTab projectId={id} project={project} onSaved={() => api.getProject(id).then((res) => setProject(res.data))} />
        </TabsContent>

        {/* Customer Access / Sharing */}
        <TabsContent value="sharing" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold tracking-tight">Kundåtkomst</h3>
            <Button variant="outline" size="sm" onClick={() => setShareDialog(true)} data-testid="share-from-tab-btn">
              <Share2 size={14} className="mr-1.5" /> Bjud in kund
            </Button>
          </div>
          <div className="bg-card border border-border rounded">
            {customerAccess.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Inga kunder har åtkomst till detta projekt ännu</div>
            ) : (
              <div className="divide-y divide-border">
                {customerAccess.map((ca) => {
                  const link = `${window.location.origin}/customer/project/${ca.access_token}`;
                  return (
                    <div key={ca.id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{ca.customer_name || ca.customer_email}</p>
                        <p className="text-xs text-muted-foreground">{ca.customer_email}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(link); toast.success('Länk kopierad!'); }} data-testid={`copy-link-${ca.id}`}>
                        <Copy size={12} className="mr-1.5" /> Kopiera länk
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Progress Dialog */}
      <Dialog open={progressDialog} onOpenChange={setProgressDialog}>
        <DialogContent data-testid="progress-dialog">
          <DialogHeader><DialogTitle>Uppdatera progress</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Progress: {progressValue}%</span>
            </div>
            <input type="range" min="0" max="100" value={progressValue} onChange={(e) => setProgressValue(parseInt(e.target.value))}
              className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-[hsl(16,100%,55%)]" data-testid="progress-slider" />
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${progressValue >= 80 ? 'bg-green-500' : progressValue >= 40 ? 'bg-yellow-500' : 'bg-[hsl(16,100%,55%)]'}`} style={{ width: `${progressValue}%` }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressDialog(false)}>Avbryt</Button>
            <Button onClick={handleUpdateProgress} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="save-progress-btn">Spara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader><DialogTitle>Dela projekt med kund</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-1.5">
              <Label>Kundens e-post</Label>
              <Input value={shareForm.customer_email} onChange={(e) => setShareForm(f => ({ ...f, customer_email: e.target.value }))} placeholder="kund@foretag.se" data-testid="share-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Kundens namn (valfritt)</Label>
              <Input value={shareForm.customer_name} onChange={(e) => setShareForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="Namn" data-testid="share-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialog(false)}>Avbryt</Button>
            <Button onClick={handleShareProject} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="share-submit-btn">Skapa länk</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Dialog (Mocked BankID) */}
      <Dialog open={signDialog} onOpenChange={setSignDialog}>
        <DialogContent data-testid="sign-dialog">
          <DialogHeader><DialogTitle>Signera med BankID (Mockad)</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              I produktion kommer detta att kopplas till riktigt BankID. Just nu simuleras signeringen.
            </p>
            <div className="space-y-1.5">
              <Label>Typ av signering</Label>
              <select value={signAction} onChange={(e) => setSignAction(e.target.value)} className="w-full h-10 px-3 border border-input rounded text-sm bg-background" data-testid="sign-action-select">
                <option value="project_complete">Projekt klart</option>
                <option value="day_complete">Dag avslutad</option>
                <option value="eta_approved">ÄTA godkänd</option>
                <option value="invoice_approved">Faktura godkänd</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialog(false)}>Avbryt</Button>
            <Button onClick={handleSign} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="sign-submit-btn">
              <Pen size={14} className="mr-1.5" /> Signera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Cost Estimate Dialog (#6) */}
      <Dialog open={showCostEstimate} onOpenChange={setShowCostEstimate}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Kostnadsberäkning</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Beskriv jobbet</Label>
              <Textarea
                value={estimateDesc}
                onChange={e => setEstimateDesc(e.target.value)}
                placeholder="T.ex. Renovering av badrum 8m2, inklusive rivning, nytt kakel, VVS och el..."
                rows={3}
                data-testid="estimate-desc-input"
              />
            </div>
            <Button onClick={handleAiEstimate} disabled={costEstimating} className="bg-[#E8613A] hover:bg-[#d4552f] text-white w-full" data-testid="run-estimate-btn">
              {costEstimating ? <><Loader2 size={14} className="mr-1 animate-spin" /> Beräknar...</> : <><Brain size={14} className="mr-1" /> Beräkna kostnad</>}
            </Button>

            {aiEstimate && (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 dark:bg-blue-950/30 rounded p-3">
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Material</p>
                    <p className="text-lg font-bold">{aiEstimate.material?.total?.toLocaleString('sv-SE')} kr</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 rounded p-3">
                    <p className="text-xs font-medium text-green-600 dark:text-green-400">Arbete</p>
                    <p className="text-lg font-bold">{aiEstimate.labor?.total?.toLocaleString('sv-SE')} kr</p>
                    <p className="text-xs text-muted-foreground">{aiEstimate.labor?.hours}h × {aiEstimate.labor?.hourly_rate} kr</p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-950/30 rounded p-3">
                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Maskiner</p>
                    <p className="text-lg font-bold">{aiEstimate.machines?.total?.toLocaleString('sv-SE')} kr</p>
                  </div>
                </div>
                <div className="border-t pt-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-xl font-bold">{aiEstimate.total_cost?.toLocaleString('sv-SE')} kr</p>
                  </div>
                  {aiEstimate.rot_deduction > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-emerald-600">Efter ROT</p>
                      <p className="text-lg font-bold text-emerald-600">{aiEstimate.total_after_rot?.toLocaleString('sv-SE')} kr</p>
                    </div>
                  )}
                </div>
                {aiEstimate.summary && <p className="text-sm text-muted-foreground bg-muted/50 rounded p-3">{aiEstimate.summary}</p>}
                <Button onClick={applyEstimateToBudget} className="w-full" data-testid="apply-estimate-btn">
                  Applicera på projektbudget
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
